import React, { Component, lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import { Switch, Route } from 'react-router-dom';
import { environment } from '../config/appEnvironment';

import * as actionTypes from '../store/constants/actionTypes';

import * as UserHelper from '../utils/helpers/UserHelper';
import * as peopleStructureHelper from '../utils/helpers/peopleStructureHelper';
import * as CookieHelper from '../utils/helpers/CookieHelper';
import * as SalesforceAuthenticator from '../utils/services/salesforceAuthenticator';
import amplitude from '../utils/services/amplitude';

import * as Sf from '../utils/services/salesforce';

const LeaderHub = lazy(() => import('./LeaderHub/LeaderHub'));
const LeaderShiftEdit = lazy(() => import('./LeaderShiftEdit/LeaderShiftEdit'));
const MemberShiftEdit = lazy(() => import('./MemberShiftEdit/MemberShiftEdit'));
const MemberHub = lazy(() => import('./MemberHub/MemberHub'));
const Encanta21MagicEyeSelector = lazy(() =>
  import('./Encanta21MagicEye/Encanta21MagicEyeSelector')
);
const HealthCheck = lazy(() => import('./HealthCheck/HealthCheck'));
const MemberProfileChange = lazy(() => import('./MemberProfileChange/MemberProfileChange'));
const MemberProfileChangeList = lazy(() =>
  import('./MemberProfileChangeList/MemberProfileChangeList')
);
const MemberProfileChangeBySheet = lazy(() =>
  import('./MemberProfileChangeBySheet/MemberProfileChangeBySheet')
);
const MassiveHesps = lazy(() => import('./MassiveHesp/MassiveHesp'));
const Encanta21Contestation = lazy(() => import('./Encanta21Contestation/Encanta21Contestation'));
const ShiftsInput = lazy(() => import('./Shifts/ShiftsInput/ShiftsInput'));
const WeekendShiftsOutput = lazy(() => import('./Shifts/WeekendShiftsOutput/WeekendShiftsOutput'));
const LoginCallback = lazy(() => import('./LoginCallback/LoginCallback'));
const VacationRequests = lazy(() => import('./VacationRequests/VacationRequests'));

const { REACT_APP_CALLBACK_URL } = environment;

class Routes extends Component {
  state = {
    recordId: undefined,
    loaded: false,
    isReduxInfoSet: false,
    isLoadingSSId: false
  };

  componentDidMount() {
    console.log('[React] Did mount');
    window.addEventListener('message', this.handleMessageEvent);
    window.addEventListener('storage', this.handleLocalStorageFulfillment);
    this.loginOAuth();
  }

  componentDidUpdate(prevProps) {
    console.log('[React] Did update');
    const { shouldRefreshToken } = this.props;

    if (shouldRefreshToken && prevProps.shouldRefreshToken !== shouldRefreshToken) {
      this.loginOAuth();
    }
  }

  componentWillUnmount() {
    console.log('[React] Will unmount');
    window.removeEventListener('message', this.handleMessageEvent);
    window.removeEventListener('storage', this.handleLocalStorageFulfillment);
  }

  handleMessageEvent = async event => {
    console.log('[React] Received message event:', event);

    if (event.data && event.data.call === 'sfSendValue') {
      // const salesforceUser = await Sf.configureConnection(event.data.value);
      this.setState({ recordId: event.data.recordId });
    }
  };

  handleLocalStorageFulfillment = () => {
    if (localStorage.getItem('userId')) {
      localStorage.removeItem('userId');
      this.loginOAuth();
    }
  };

  loginOAuth = async () => {
    const accessToken = CookieHelper.getCookie('new_ssid');
    const refreshToken = CookieHelper.getCookie('rfTk');
    const userId = CookieHelper.getCookie('userId');

    if (refreshToken && !accessToken) {
      SalesforceAuthenticator.getStneRefreshedToken().then(this.fulfillSalesforceUser);
    } else if (!accessToken && !refreshToken) {
      CookieHelper.setCookie('new_ssid', 'loading', 1);
      this.setState({ isLoadingSSId: true });

      if (window.location.pathname !== '/oauth/salesforce/cb') {
        SalesforceAuthenticator.getStneOauthSalesforceConfigurations()
          .then(({ data }) => {
            const { clientId, url } = data;

            const params = new URLSearchParams({
              client_id: clientId,
              redirect_uri: REACT_APP_CALLBACK_URL,
              response_type: 'code'
            });

            window.open(`${url}/services/oauth2/authorize?${params.toString()}`, '_blank');
          })
          .catch(err => {
            console.error(err);
            CookieHelper.removeCookie('new_ssid');
          });
      }
    } else if (accessToken !== 'loading' && userId) {
      try {
        await this.fulfillSalesforceUser();
        this.setState({ isReduxInfoSet: true });
      } catch {
        this.removeCookieAndCallOAuthFn();
      }
    } else {
      this.removeCookieAndCallOAuthFn();
    }
  };

  clearRedux = () => {
    this.props.setSalesforceUser({});
    this.props.setUserProfile(null, null);
  };

  fulfillSalesforceUser = () =>
    new Promise((resolve, reject) => {
      const accessToken = CookieHelper.getCookie('new_ssid');
      const myUserId = CookieHelper.getCookie('userId');

      this.clearRedux();

      const salesforceUser = Sf.configureConnection(`${myUserId};${accessToken}`);
      this.props.setShouldRefreshToken(false);
      this.props.setSalesforceUser(salesforceUser);

      if (this.state.isReduxInfoSet) {
        return resolve(
          'As informações do Redux já estão setadas e as credenciais do SF User já foram atualizadas.'
        );
      }

      const promisesAndCbFns = [
        [peopleStructureHelper.getAllMembers, this.props.setSalesforceMembers],
        [peopleStructureHelper.getAllUsers, this.props.setAllUsers],
        [peopleStructureHelper.getAllProfiles, this.props.setAllProfiles],
        [peopleStructureHelper.getAllRoles, this.props.setAllRoles],
        [peopleStructureHelper.getAllChannels, this.props.setAllChannels],
        [UserHelper.getUserMember, this.userMemberCbFn],
        [UserHelper.getUserProfile, this.userProfileCbFn],
        [peopleStructureHelper.getAllSquads, this.squadsCbFn]
      ];

      Promise.all(promisesAndCbFns.map(([myPromise]) => myPromise(salesforceUser)))
        .then(res => {
          for (const [i, promiseRes] of Object.entries(res)) {
            const index = Number(i);
            const cbFn = promisesAndCbFns[index][1];
            cbFn(promiseRes, salesforceUser);
          }
          console.log('[React] Consegui setar todas as informações do Redux');
          resolve();
        })
        .catch(err => {
          console.error(err);
          reject(err);
        });
    });

  userMemberCbFn = async userMember => {
    this.setState({ loaded: true });
    const { Canal_de_Atendimento__c: userChannel, Email_do_SalesForce__c: userEmail } = userMember;
    this.props.setUserChannel(userChannel);
    this.props.setUserMember(userMember);
    amplitude.setUserId(userEmail);

    const adminsEmails = await UserHelper.getAdmins();
    this.props.setUserIsAdmin(userEmail && adminsEmails.some(email => email === userEmail));
  };

  userProfileCbFn = ({ name, id, email }, salesforceUser) => {
    // Finishes setting salesforceUser on Redux
    amplitude.setProfile(name);
    this.props.setSalesforceUser({ ...salesforceUser, name: email });
    console.log('[SV] SalesforceUser Preenchido!');
    this.props.setUserProfile(name, id);
  };

  squadsCbFn = ({ squads, squadsNames }, salesforceUser) => {
    this.props.setSalesforceSquads(squads, squadsNames);

    const leaderSquads = squads.filter(squad => {
      return squad.Lider__r.Name__c === salesforceUser.id;
    });

    this.props.setUserIsLeader(leaderSquads.length > 0);
    this.props.setLeadershipSquad(leaderSquads.length > 0 ? leaderSquads[0] : null);
  };

  removeCookieAndCallOAuthFn = () => {
    CookieHelper.removeCookie('new_ssid');
    this.loginOAuth();
  };

  render() {
    const { recordId, loaded } = this.state;

    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Switch>
          <Route
            exact
            path="/oauth/salesforce/cb"
            render={() => <LoginCallback fulfillSalesforceUser={this.fulfillSalesforceUser} />}
          />
          <Route path="/healthz" component={HealthCheck} />
          <Route path="/member_profile_change" component={MemberProfileChange} />
          <Route path="/profile_change_canceling" component={MemberProfileChangeList} />
          <Route path="/profile_change_sheet" component={MemberProfileChangeBySheet} />
          <Route path="/massive_hesps" component={MassiveHesps} />
          {loaded && (
            <>
              <Route exact path="/" component={LeaderHub} />
              <Route exact path="/member_shift_edit" component={MemberShiftEdit} />
              <Route
                path="/leader_shift_edit"
                render={() => <LeaderShiftEdit memberId={recordId} />}
              />
              <Route path="/leader_hub" component={LeaderHub} />
              <Route path="/member_hub" component={MemberHub} />
              <Route path="/encanta21_magic_eye" component={Encanta21MagicEyeSelector} />
              <Route path="/encanta21_contestation" component={Encanta21Contestation} />
              <Route path="/shifts_input" component={ShiftsInput} />
              <Route path="/weekend_shifts_output" component={WeekendShiftsOutput} />
              <Route
                path="/wrkf_scheduled_absence_requests"
                render={() => <VacationRequests judge />}
              />
              <Route
                path="/wrkf_member_vacation_requests"
                render={() => <VacationRequests member />}
              />
            </>
          )}
        </Switch>
      </Suspense>
    );
  }
}

const mapDispatch = dispatch => {
  return {
    setSalesforceUser: salesforceUser =>
      dispatch({ type: actionTypes.SET_SALESFORCE_USER, salesforceUser }),
    setUserProfile: (name, id) => dispatch({ type: actionTypes.SET_USER_PROFILE, name, id }),
    setUserChannel: channel => dispatch({ type: actionTypes.SET_USER_CHANNEL, channel }),
    setSalesforceMembers: salesforceMembers =>
      dispatch({ type: actionTypes.SET_SALESFORCE_MEMBERS, salesforceMembers }),
    setSalesforceSquads: (squads, squadsNames) =>
      dispatch({ type: actionTypes.SET_SALESFORCE_SQUADS, squads, squadsNames }),
    setUserIsLeader: userIsLeader =>
      dispatch({ type: actionTypes.SET_USER_IS_LEADER, userIsLeader }),
    setUserIsAdmin: userIsAdmin => dispatch({ type: actionTypes.SET_USER_IS_ADMIN, userIsAdmin }),
    setLeadershipSquad: leadershipSquad =>
      dispatch({ type: actionTypes.SET_LEADERSHIP_SQUAD, leadershipSquad }),
    setUserMember: member => dispatch({ type: actionTypes.SET_USER_MEMBER, member }),
    setAllUsers: allUsers => dispatch({ type: actionTypes.SET_ALL_USERS, allUsers }),
    setAllProfiles: allProfiles => dispatch({ type: actionTypes.SET_ALL_PROFILES, allProfiles }),
    setAllRoles: allRoles => dispatch({ type: actionTypes.SET_ALL_ROLES, allRoles }),
    setAllChannels: allChannels => dispatch({ type: actionTypes.SET_ALL_CHANNELS, allChannels }),
    setShouldRefreshToken: shouldRefresh =>
      dispatch({ type: actionTypes.SET_SHOULD_REFRESH_TOKEN, shouldRefresh })
  };
};

export default connect(null, mapDispatch)(Routes);
